/* eslint-disable react/require-default-props */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@medflex/shared-types';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import getStatusAndChangedBy from '@medflex/utils/pamRequests/getStatusAndChangedBy';
import { FlagIcon as FlagIconOutline, ClockIcon } from '@heroicons/react/24/outline';
import { FlagIcon as FlagIconSolid, CheckIcon } from '@heroicons/react/24/solid';
import Tooltip from '../Tooltip';

export const InquiryStatusLabelVariants = {
  TEXT: 'text',
  ICON: 'icon',
  TEXT_ICON: 'text_icon',
};

const VARIANTS = {
  [RequestStatus.CLOSED]: {
    icon: <CheckIcon className="w-4 h-4" />,
    color: 'border-green-300 bg-green-100 text-green-800',
  },
  [RequestStatus.OPEN]: {
    icon: <FlagIconOutline className="w-4 h-4" />,
    color: 'border-blue-300 bg-blue-100 text-blue-800',
  },
  [RequestStatus.PROGRESS]: {
    icon: <FlagIconSolid className="w-4 h-4" />,
    color: 'border-orange-300 bg-orange-100 text-orange-800',
  },
  [RequestStatus.WAITING_FOR_ANSWER]: {
    icon: <ClockIcon className="w-4 h-4" />,
    color: 'border-yellow-300 bg-yellow-100 text-yellow-800',
  },
};

const InquiryStatusLabel = ({
  icon = null,
  isPatient = false,
  status = '',
  statusChangedBy = '',
  statusChangedAt = '',
  variant = InquiryStatusLabelVariants.TEXT,
  hasTooltip = false,
}) => {
  const { t } = useTranslation('components');
  const matchedVariant =
    isPatient && status !== RequestStatus.CLOSED ? VARIANTS[RequestStatus.OPEN] : VARIANTS[status];
  const matchedVariantLabel =
    isPatient && status !== RequestStatus.CLOSED
      ? t('PamInquiry.statusLabel.patientProgress')
      : t(`PamInquiry.statusLabel.${status}`);

  const cn = classNames([
    'flex',
    'items-center',
    'rounded',
    'border',
    'inline',
    variant === InquiryStatusLabelVariants.ICON ? ['p-0.5'] : ['px-1', 'py-1'],
    'font-semibold',
    'max-w-xs',
    'space-x-2',
    'text-sm',
    'hover:shadow-sm',
    matchedVariant.color,
  ]);

  const statusLabel = () => {
    if (variant === InquiryStatusLabelVariants.TEXT_ICON) {
      return (
        <>
          {matchedVariant.icon} <span>{matchedVariantLabel}</span>
        </>
      );
    }

    if (variant === InquiryStatusLabelVariants.TEXT) {
      return <span>{matchedVariantLabel}</span>;
    }

    return matchedVariant.icon;
  };

  return (
    <div className={cn}>
      <span className="flex space-x-1 items-center whitespace-nowrap">
        {hasTooltip ? (
          <Tooltip
            title={getStatusAndChangedBy(matchedVariantLabel, statusChangedBy, statusChangedAt)}
          >
            {statusLabel()}
          </Tooltip>
        ) : (
          statusLabel()
        )}
      </span>
      {icon}
    </div>
  );
};

InquiryStatusLabel.propTypes = {
  icon: PropTypes.element,
  isPatient: PropTypes.bool,
  status: PropTypes.string,
  statusChangedBy: PropTypes.string,
  statusChangedAt: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(InquiryStatusLabelVariants)),
  hasTooltip: PropTypes.bool,
};

export default InquiryStatusLabel;
